import React, { useState, useEffect, useCallback } from 'react'
import { GameType } from './ResultsDoughnutChartTypes'
import styles from './results-summary-section.module.scss'
import i18n from 'constants/strings'
import FilterDropdown from '../../generic/FilterDropdown'
import { matchTypes, TABLET_BREAKPOINT } from 'constants/global'
import ResultsDoughnutChart from './ResultsDoughnutChart'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAggregatedResults, setDateFilterRange } from 'actions/results-actions'
import { AppState } from 'redux/reducers'
import useWindowSize from '../../../hooks/use-windowSize'
import ChartMobileFilter from '../../generic/MobileFilterSelector/MobileFilterSelector'
import { createDateFilterOptions } from 'components/generic/DateFilterBar'
import cx from 'classnames'

interface ResultsSummarySectionProps {
  title: string
  playerId: string
  defaultGameType?: GameType
  showTitle?: boolean
  showSubTitle?: boolean
  showDateFilter?: boolean
  showGameTypeSelector?: boolean
  defaultStartDateInput?: string
}

const ResultsSummarySection: React.FC<ResultsSummarySectionProps> = ({ title, playerId, defaultGameType = 'BOTH', showTitle = true, showSubTitle = true, showDateFilter = true, showGameTypeSelector = true, defaultStartDateInput }) => {
  const dispatch = useDispatch()
  const data = useSelector((state: AppState) => state.results.resultsSummary)
  const selectedMs = useSelector((state: AppState) => state.results.dateFilter)
  const fetchFilteredResults = useCallback(
    (dateFilter: number | null) => dispatch(fetchAggregatedResults(playerId, dateFilter, defaultStartDateInput)),
    [playerId, dispatch]
  )
  const setSelectedMs = useCallback(o => dispatch(setDateFilterRange(o)), [dispatch])

  const [gameType, setGameType] = useState<GameType>(defaultGameType)
  const windowWidth = useWindowSize().width

  const chartTitle = (type: GameType) => {
    switch (type.toLowerCase()) {
      case 'singles':
        return i18n.t('strCharts.singlesWinLoss')
      case 'doubles':
        return i18n.t('strCharts.doublesWinLoss')
      case 'both':
        return i18n.t('strCharts.bothWinLoss')
    }
  }

  useEffect(() => {
    fetchFilteredResults(selectedMs)
  }, [fetchFilteredResults, selectedMs])

  return (
    <>
      {title && showTitle && (
        <h2 className={cx(styles.style2, 'results-summary-section_style2--override')}>{title}</h2>
      )}
      {showSubTitle && (
        <h3 className={cx(styles.style2, 'results-summary-section_style2--override')}>
          {chartTitle(gameType)}
        </h3>
      )}
      {windowWidth && windowWidth >= TABLET_BREAKPOINT && (
        <>
        {showDateFilter && (
          <FilterDropdown
            options={createDateFilterOptions([6, 12, 24], true)}
            selectedKey={selectedMs}
            onSelect={({ key }) => setSelectedMs?.(key)}
            />
        )}
        {showGameTypeSelector && (
          <FilterDropdown
            options={matchTypes}
            selectedKey={gameType}
            onSelect={({ key }) => setGameType(key)}
            style={{ marginLeft: '20px' }}
          />
        )}
        </>
      )}

      {windowWidth && windowWidth < TABLET_BREAKPOINT && (
        <ChartMobileFilter
          gameTypeFilterData={{
            gameTypeItems: matchTypes,
            selectedGameTypeItem: gameType,
            gameTypeDispatch: setGameType,
          }}
          timePeriodFilterData={{
            timePeriodItems: createDateFilterOptions([6, 12, 24], true),
            selectedTimePeriodItem: selectedMs,
            updateTimePeriodFilterValue: setSelectedMs,
          }}
          classNames={{ filterBar: styles.filterBar }}
        />
      )}

      <ResultsDoughnutChart data={data} type={gameType} />
    </>
  )
}

export default ResultsSummarySection
